import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  RequireIcon as CustomRequireIcon,
  DropDownArrowIcon,
} from "@ippon/shared-assets";
import { useResponsiveMenuPlacement } from "../../hooks/useResponsiveMenuPlacement";

export interface CategoryOption {
  value: string;
  label: string;
  /** 指定時、オプション右端にグレーで表示 (左=label / 右=rightLabel の between)。 */
  rightLabel?: string;
}

export interface CustomCategorySelectProps {
  label: string;
  name: string;
  options: CategoryOption[];
  selectedCategories: string[];
  onCategoriesChange: (categories: string[]) => void;
  errors?: any;
  isRequired?: boolean;
  labelWidth?: string;
  inputWidth?: string;
  placeholder?: string;
  onAddNewCategory?: (newCategory: string) => void;
  onDeleteCategoryOption?: (categoryValue: string) => void;
  /** 検索ボックスを非表示にする (= 絞り込み機能を消す) */
  hideSearch?: boolean;
}

const CustomCategorySelect: React.FC<CustomCategorySelectProps> = ({
  label,
  name,
  options,
  selectedCategories,
  onCategoriesChange,
  errors = {},
  isRequired = false,
  labelWidth = "120px",
  inputWidth = "100%",
  placeholder = "選択してください",
  onAddNewCategory,
  onDeleteCategoryOption: _onDeleteCategoryOption,
  hideSearch = false,
}) => {
  // 検索ボックスのplaceholderを追加機能の有無に応じて設定
  const searchPlaceholder = onAddNewCategory ? "検索と追加" : "キーワード検索";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingCategory, setPendingCategory] = useState("");
  const openMenu = Boolean(anchorEl);
  const {
    menuMaxHeight,
    menuAnchorOrigin,
    menuTransformOrigin,
    updateMenuPlacement,
  } = useResponsiveMenuPlacement(400);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    const trigger = event.currentTarget;
    updateMenuPlacement(trigger);
    setAnchorEl(trigger);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSearchText("");
    setShowConfirmation(false);
    setPendingCategory("");
  };

  const toggleCategory = (categoryValue: string) => {
    const isSelected = selectedCategories.includes(categoryValue);
    if (isSelected) {
      onCategoriesChange(selectedCategories.filter((c) => c !== categoryValue));
    } else {
      onCategoriesChange([...selectedCategories, categoryValue]);
    }
  };

  const removeCategory = (categoryValue: string) => {
    onCategoriesChange(selectedCategories.filter((c) => c !== categoryValue));
  };

  const handleAddNewCategoryClick = () => {
    const trimmed = searchText.trim();
    if (!trimmed || !onAddNewCategory) return;

    // 既存に存在しているものならそれを選択して追加
    const existing = options.find(
      (opt) => opt.label.toLowerCase() === trimmed.toLowerCase(),
    );

    if (existing) {
      const alreadySelected = selectedCategories.includes(existing.value);
      if (!alreadySelected) {
        onCategoriesChange([...selectedCategories, existing.value]);
      }
      setSearchText("");
      setShowConfirmation(false);
      setPendingCategory("");
      return;
    }

    // 新規として追加
    onAddNewCategory(trimmed);
    setSearchText("");
    setShowConfirmation(false);
    setPendingCategory("");
  };

  const handleConfirmAdd = () => {
    if (pendingCategory && onAddNewCategory) {
      onAddNewCategory(pendingCategory);
      setSearchText("");
      setPendingCategory("");
      setShowConfirmation(false);
    }
  };

  const handleCancelAdd = () => {
    setPendingCategory("");
    setShowConfirmation(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key === "Enter" &&
      onAddNewCategory &&
      searchText.trim() !== "" &&
      !showConfirmation
    ) {
      handleAddNewCategoryClick();
    }
  };

  // 検索フィルタリング
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchText.toLowerCase()),
  );

  // 新規追加可能かどうかの判定（UI表示条件は endAdornment 側で判定）
  // 以前は canAddNew を UI 表示にも使用していたが、
  // 現在は入力中は常時ボタンを表示するため使用しない。

  return (
    <Box
      className="inputGroupWrapper flexCol"
      sx={{
        display: "flex !important",
        flexDirection: "column !important",
        // mt: 1,
      }}
    >
      <Box
        className="inputLabel"
        sx={{
          display: "flex",
          alignItems: "center",
          width: { lg: labelWidth, xs: "100%" },
          fontSize: "12px !important",
          alignSelf: "flex-start",
          pb: "4px",
          mb: "1px",
        }}
      >
        {label}
        {isRequired && (
          <span style={{ display: "flex", marginLeft: "4px" }}>
            <CustomRequireIcon />
          </span>
        )}
      </Box>
      <Box sx={{ width: { sm: inputWidth, xs: "100%" } }}>
        <Box
          onClick={handleOpenMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 0.5,
            minHeight: 26,
            px: 0.5,
            py: 0.5,
            bgcolor: "#fff",
            border: "1px solid #D9D9D9",
            borderRadius: "8px",
            cursor: "pointer",
            flexWrap: "wrap",
          }}
        >
          {selectedCategories.length === 0 && (
            <Box sx={{ fontSize: 12, color: "#989898", px: 0.75 }}>
              {placeholder}
            </Box>
          )}
          {selectedCategories.map((c) => (
            <Box
              key={c}
              sx={{
                bgcolor: "#F0F0F0",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                height: 24,
                px: 1,
                borderRadius: "4px",
              }}
            >
              <Box sx={{ fontSize: 12, color: "#3E3E3E" }}>
                {options.find((o) => o.value === c)?.label ?? c}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeCategory(c);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  width: 16,
                  height: 16,
                  border: "none",
                  background: "transparent",
                  borderRadius: "50%",
                  cursor: "pointer",
                  p: 0,
                  transition: "background-color 0.2s",
                  "&:hover": {
                    backgroundColor: "#d9d9d9",
                  },
                }}
                aria-label={`${c} を削除`}
              >
                <CloseIcon sx={{ fontSize: 12, color: "#3E3E3E" }} />
              </Box>
            </Box>
          ))}
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              pr: 0.75,
              pl: 0.5,
              transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
              transform: openMenu ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <DropDownArrowIcon />
          </Box>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={menuAnchorOrigin}
          transformOrigin={menuTransformOrigin}
          transitionDuration={0}
          PaperProps={{
            sx: {
              // 幅はオプションの中身に合わせて広げる (max-content)。
              // ただしトリガー幅を下限にして狭くはならないようにする。
              minWidth: anchorEl?.offsetWidth || "auto",
              width: "max-content",
              maxWidth: "calc(100vw - 32px)",
              maxHeight: menuMaxHeight,
            },
          }}
        >
          {/* 検索ボックス・確認UI（上部に配置） */}
          {!hideSearch && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderBottom: "1px solid #F0F0F0",
                bgcolor: "#FAFAFA",
              }}
            >
              {showConfirmation ? (
                // 確認モード：タグ + 追加リンクのみ
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: 36,
                    px: 1.25,
                    py: 0.5,
                    bgcolor: "#fff",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#F0F0F0",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      height: 24,
                      px: 1,
                      borderRadius: "4px",
                    }}
                  >
                    <Box sx={{ fontSize: 12, color: "#3E3E3E" }}>
                      {pendingCategory}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelAdd();
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        width: 16,
                        height: 16,
                        border: "none",
                        background: "transparent",
                        borderRadius: "50%",
                        cursor: "pointer",
                        p: 0,
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: "#d9d9d9",
                        },
                      }}
                      aria-label="キャンセル"
                    >
                      <CloseIcon sx={{ fontSize: 12, color: "#3E3E3E" }} />
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirmAdd();
                    }}
                    sx={{
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: 12,
                      color: "#0E9DBC",
                      fontWeight: 600,
                      textDecoration: "underline",
                      "&:hover": {
                        color: "#0B7A8F",
                      },
                    }}
                  >
                    追加する
                  </Box>
                </Box>
              ) : (
                // 検索モード：通常のTextField
                <TextField
                  fullWidth
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#989898" }} />
                      </InputAdornment>
                    ),
                    endAdornment: onAddNewCategory &&
                      searchText.trim() !== "" && (
                        <InputAdornment position="end">
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddNewCategoryClick();
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              pr: 0.5,
                              py: 0.2,
                              borderRadius: "10px",
                              transition: "all 0.2s",
                              bgcolor: "#f7fbfd",
                              border: "1px solid #0B9DBD",
                              color: "#0B9DBD",
                              fontFamily: '"Noto Sans JP"',
                              fontWeight: 700,
                              "&:hover": {
                                bgcolor: "#e9f6fa",
                                borderColor: "#0B9DBD",
                              },
                            }}
                          >
                            <IconButton
                              size="small"
                              disableRipple
                              className="add-icon"
                              sx={{
                                p: 0.4,
                                color: "#0B9DBD",
                                borderRadius: "6px",
                                transition: "all 0.2s",
                              }}
                              aria-label="新規追加"
                              title="新規追加"
                            >
                              <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Box
                              component="span"
                              className="add-text"
                              sx={{
                                fontSize: 12,
                                color: "#0B9DBD",
                                fontWeight: 700,
                                transition: "all 0.2s",
                                pr: 0.7,
                              }}
                            >
                              追加
                            </Box>
                          </Box>
                        </InputAdornment>
                      ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 12,
                      height: 36,
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#D9D9D9",
                      },
                      "&:hover fieldset": {
                        borderColor: "#D9D9D9",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#D9D9D9",
                      },
                    },
                  }}
                />
              )}
            </Box>
          )}

          {/* フィルタリングされたカテゴリ一覧 */}
          <Box sx={{ maxHeight: "250px", overflowY: "auto" }}>
            {filteredOptions.length === 0 ? (
              <MenuItem disabled sx={{ fontSize: 12, py: 1 }}>
                該当する項目がありません
              </MenuItem>
            ) : (
              filteredOptions.map((opt) => {
                const checked = selectedCategories.includes(opt.value);
                return (
                  <MenuItem
                    key={opt.value}
                    onClick={() => toggleCategory(opt.value)}
                    sx={{ fontSize: 12, py: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      sx={{
                        mr: 1,
                        color: "#d0d0d0",
                        "&.Mui-checked": {
                          color: "#0B9DBD",
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>{opt.label}</Box>
                  </MenuItem>
                );
              })
            )}
          </Box>
        </Menu>
      </Box>
      {errors && errors[name] && (
        <Box
          sx={{
            color: "error.main",
            fontSize: "12px",
            mt: 0.5,
          }}
        >
          {errors[name].message}
        </Box>
      )}
    </Box>
  );
};

export default CustomCategorySelect;
